import { useEffect } from 'react';
import { Link } from 'react-router';
import { useOverview } from '../api/queries.js';
import { Badge, Card, Loading, Page, Problem, Stat, Table, toneForStatus } from '../components/ui.jsx';
import { FlowDiagram } from '../features/live/FlowDiagram.jsx';
import { useTraffic } from '../hooks/useTraffic.js';
import { trafficStore } from '../stores/traffic.js';
import { formatDuration, formatNumber, formatTime } from '../utils/format.js';

/**
 * The first screen: the state of the whole ecosystem in one look. The numbers come from one
 * request at load, and from then on the page follows the live stream — it never polls.
 */
export function Dashboard() {
  const overview = useOverview();
  const traffic = useTraffic();

  useEffect(() => {
    if (overview.data) trafficStore.seed(overview.data.sequence);
  }, [overview.data]);

  if (overview.isLoading) return <Loading what="la dashboard" />;
  if (overview.error) return <Problem error={overview.error} />;
  const data = overview.data!;

  const failing = data.messages.failed + data.messages.deadLettered;

  return (
    <Page eyebrow="OSSERVA" title="Dashboard" description="Le ultime 24 ore di traffico fra le applicazioni.">
      <div className="stat-row">
        <Stat label="Messaggi pubblicati" value={formatNumber(data.messages.published)} />
        <Stat label="Elaborati" value={formatNumber(data.messages.processed)} tone="good" />
        <Stat label="In errore" value={formatNumber(failing)} tone={failing > 0 ? 'bad' : 'neutral'} hint={`${formatNumber(data.messages.deadLettered)} in dead letter`} />
        <Stat label="Latenza mediana" value={formatDuration(data.messages.p50ProcessingMs)} hint={`p95 ${formatDuration(data.messages.p95ProcessingMs)}`} />
        <Stat label="Applicazioni" value={`${data.applications.connected}/${data.applications.total}`} hint="connesse" />
        <Stat label="Canali" value={formatNumber(data.channels.total)} hint={`${formatNumber(data.channels.archived)} archiviati`} />
      </div>

      <Card title="Traffico in tempo reale" hint="Ogni pallino è un messaggio che attraversa Broker adesso">
        <FlowDiagram events={traffic.events} />
      </Card>

      <div className="grid-2">
        <Card title="Volume per ora">
          <Sparkline points={data.throughput.map((point) => point.count)} />
          <p className="muted small">
            {data.throughput.length === 0 ? 'Nessun messaggio nelle ultime 24 ore.' : `${formatNumber(data.messages.published)} messaggi in ${data.throughput.length} ore.`}
          </p>
        </Card>

        <Card title="Canali più attivi">
          <Table head={<tr><th>Canale</th><th className="right">Messaggi</th></tr>} empty="Nessun canale ha ancora traffico.">
            {data.topChannels.map((channel) => (
              <tr key={channel.channel}>
                <td>
                  <Link to={`/messages?channel=${encodeURIComponent(channel.channel)}`}>{channel.channel}</Link>
                </td>
                <td className="right">{formatNumber(channel.count)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>

      <Card title="Ultimi eventi" hint="Dalla connessione live">
        <Table
          head={
            <tr>
              <th>Ora</th>
              <th>Evento</th>
              <th>Canale</th>
              <th>Da</th>
              <th>A</th>
              <th className="right">Durata</th>
            </tr>
          }
          empty="In attesa del primo messaggio."
        >
          {traffic.events.slice(0, 12).map((event) => (
            <tr key={event.sequence}>
              <td className="mono">{formatTime(event.occurredAt)}</td>
              <td>
                <Badge tone={toneForStatus(event.type.replace('message.', ''))}>{event.type.replace('message.', '')}</Badge>
              </td>
              <td>{event.channel ?? '—'}</td>
              <td>{event.from ?? '—'}</td>
              <td>{event.to ?? '—'}</td>
              <td className="right">{formatDuration(event.processingMs)}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}

/** A plain sparkline: no library, no axes, just the shape of the last hours. */
function Sparkline({ points }: { points: number[] }) {
  if (points.length === 0) return <div className="sparkline empty" />;
  const max = Math.max(...points, 1);
  return (
    <div className="sparkline">
      {points.map((value, index) => (
        <span key={index} style={{ height: `${Math.max(4, (value / max) * 100)}%` }} data-value={value} />
      ))}
    </div>
  );
}
